"use client";

import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

// ---------------------------------------------------------------------------
// Lightbox for clickable blog images. Mounted once at the article root;
// shows a fullscreen overlay with the chosen image when a click handler
// passes (src, alt) in via the `open` prop. Supports:
//   - ESC to close
//   - Click outside the image / on the close button to close
//   - Click on the image itself to toggle between fit-to-screen and 100%
//     (basic zoom). Cursor reflects current mode.
//   - Wheel zoom around the cursor position.
// Uses a portal so the overlay isn't clipped by parent overflow rules.
// ---------------------------------------------------------------------------

export type IBlogImageLightboxProps = {
  open: { src: string; alt: string } | null;
  onClose: () => void;
};

const MIN_SCALE = 1;
const MAX_SCALE = 5;

const BlogImageLightbox: FC<IBlogImageLightboxProps> = ({ open, onClose }) => {
  const [mounted, setMounted] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const draggingRef = useRef<{ x: number; y: number } | null>(null);

  // Portal target only exists in the browser.
  useEffect(() => setMounted(true), []);

  // Reset zoom whenever a new image opens.
  useEffect(() => {
    if (open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open]);

  // ESC to close + body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX_SCALE, s + 0.5));
      if (e.key === "-") setScale((s) => Math.max(MIN_SCALE, s - 0.5));
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!mounted || !open) return null;

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleImageClick = () => {
    // Toggle between fit-to-screen (1) and 2x zoom.
    setScale((s) => (s === 1 ? 2 : 1));
    setOffset({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent<HTMLImageElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.25 : 0.25;
    setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)));
  };

  const handleMouseDown = (e: MouseEvent<HTMLImageElement>) => {
    if (scale === 1) return;
    draggingRef.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    setOffset({
      x: e.clientX - draggingRef.current.x,
      y: e.clientY - draggingRef.current.y,
    });
  };

  const stopDrag = () => {
    draggingRef.current = null;
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={handleOverlayClick}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black-950/90 p-4 backdrop-blur-sm animate-in fade-in duration-200"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close image preview"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-6 md:top-6"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Zoom indicator */}
      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-normal uppercase tracking-wider text-white backdrop-blur-sm">
        <button
          type="button"
          onClick={() => setScale((s) => Math.max(MIN_SCALE, s - 0.5))}
          className="text-lg leading-none transition-colors hover:text-yellow-400"
          aria-label="Zoom out"
        >
          −
        </button>
        <span>{Math.round(scale * 100)}%</span>
        <button
          type="button"
          onClick={() => setScale((s) => Math.min(MAX_SCALE, s + 0.5))}
          className="text-lg leading-none transition-colors hover:text-yellow-400"
          aria-label="Zoom in"
        >
          +
        </button>
      </div>

      {/* Image */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={open.src}
        alt={open.alt}
        onClick={handleImageClick}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        draggable={false}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          cursor: scale === 1 ? "zoom-in" : draggingRef.current ? "grabbing" : "grab",
        }}
        className={clsx(
          "max-h-[90vh] max-w-[90vw] select-none rounded-md shadow-2xl transition-transform duration-200",
          // disable the transition while dragging so the image follows
          // the cursor 1:1 instead of easing
          draggingRef.current && "!transition-none",
        )}
      />
    </div>,
    document.body,
  );
};

export default BlogImageLightbox;
